import {
  isTerminalRunStatus,
  type RunStatus,
  type RunStepKind,
  type RunStepSummary,
} from '@coworker/contracts';

import { cn } from '@/lib/cn.js';

/**
 * What the employee is doing, while it is doing it — AGT-014, §21.3.
 *
 * **"An agent message must not just sit on a spinner for a long time."** That is
 * the whole requirement, and what it replaced was one line reading "Working on
 * it…" for eight different statuses — hung, in practice, over the longest part
 * of a run: the model call.
 *
 * **Every kind of step, where its predecessor showed one.** `RunTrace` filtered
 * to `kind === 'TOOL'` on the argument that the context read and the model call
 * "are ours and mean nothing to the person". That argument holds for *what they
 * are* and fails for *how long they take*: four kinds out of five were written
 * to the database with a sentence each and shown to nobody, so the part of the
 * run a person actually waits through was the invisible part.
 *
 * **The label is ours; the sentence is the runtime's.** `summaryRedacted` is
 * written for an audit trail — `1 tool(s) available; gpt-5.4-mini answered in
 * 941 ms` — and it is asserted verbatim in the deploy job, where the tool count
 * is what makes "the runtime declared a tool to the model" able to fail. So it
 * is shown beside a human label rather than rewritten: the person reads the
 * label, and the sentence is there when the answer is wrong and somebody needs
 * to know why.
 */
export interface RunTimelineProps {
  readonly steps: readonly RunStepSummary[] | undefined;
  /** Absent while the run is still being discovered. */
  readonly runStatus: RunStatus | undefined;
  /** What was asked, for the heading. §21.3's "Working on pipeline review". */
  readonly instruction: string | undefined;
}

/**
 * What each kind of step is called, running and finished.
 *
 * The same shape as `STATUS_VARIANT` in `Employees.tsx` and for the same
 * reason: a `Record` keyed by the contract's own union cannot fall behind it —
 * adding a kind to `RUN_STEP_KINDS` fails the build here rather than rendering
 * a blank line in production.
 */
const STEP_LABEL: Readonly<
  Record<RunStepKind, { readonly running: string; readonly done: string }>
> = {
  CONTEXT: { running: 'Reading the conversation…', done: 'Read the conversation' },
  MODEL: { running: 'Thinking…', done: 'Thought about it' },
  TOOL: { running: 'Using a tool…', done: 'Used a tool' },
  APPROVAL: { running: 'Waiting for you', done: 'Asked your permission' },
  VERIFY: { running: 'Writing the answer…', done: 'Wrote the answer' },
};

/**
 * §21.3's markers, and one it does not name.
 *
 * The spec lists `✓` done, `•` in progress and `⏸` waiting. A failed step has
 * no marker there because the example has no failure; `✕` is the obvious
 * fourth, and leaving a failure on `✓` would be the screen lying. All four are
 * `aria-hidden` — the label beside them already says which is which, and a
 * screen reader announcing "check mark" adds nothing.
 */
function markerFor(step: RunStepSummary): string {
  if (step.status === 'FAILED') return '✕';
  if (step.status === 'SUCCEEDED') return '✓';
  return step.kind === 'APPROVAL' ? '⏸' : '•';
}

function labelFor(step: RunStepSummary): string {
  const label = STEP_LABEL[step.kind];
  return step.status === 'RUNNING' ? label.running : label.done;
}

/**
 * How long a step took, or has been taking.
 *
 * **No timer, deliberately.** `useRun` polls every two seconds and re-renders,
 * so a running step's elapsed time advances on its own; a `setInterval` here
 * would add a second clock to keep in step with the first for a number nobody
 * reads to the tenth of a second. If this ever needs to tick faster than the
 * poll, change the poll.
 *
 * Absent rather than zero when the timestamps cannot be read: a duration of
 * `0.0s` on a step that is plainly still going is worse than no duration.
 *
 * **The clamp is the one case that still shows `0.0s`, and it is deliberate.**
 * A running step is measured against the *browser's* clock and a *server*
 * timestamp, so a machine set a minute behind would compute a negative elapsed
 * time. `Math.max` renders that as `0.0s` - wrong, but wrong in a way that
 * reads as "just started" rather than as a negative number, and it corrects
 * itself as the clock passes the start. Skew is not something this screen can
 * detect, let alone fix.
 */
export function durationMs(step: RunStepSummary, now: number): number | undefined {
  const started = Date.parse(step.startedAt);
  if (Number.isNaN(started)) return undefined;

  if (step.endedAt === undefined) return Math.max(0, now - started);

  const ended = Date.parse(step.endedAt);
  return Number.isNaN(ended) ? undefined : Math.max(0, ended - started);
}

/** Seconds to one decimal under a minute, whole minutes above it. */
export function formatDuration(ms: number): string {
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return seconds === 0 ? `${String(minutes)}m` : `${String(minutes)}m ${String(seconds)}s`;
}

/**
 * The whole run's elapsed time: the first start to the last end.
 *
 * Not a sum of the steps. A run parked on an approval for twenty hours spends
 * almost all of its wall clock between two steps rather than inside one, and a
 * sum would report that as a few seconds of work — true about the machine and
 * false about the wait.
 */
function totalMs(steps: readonly RunStepSummary[], now: number): number | undefined {
  const starts = steps.map((step) => Date.parse(step.startedAt)).filter((at) => !Number.isNaN(at));
  if (starts.length === 0) return undefined;

  const running = steps.some((step) => step.endedAt === undefined);
  const ends = steps
    .map((step) => (step.endedAt === undefined ? NaN : Date.parse(step.endedAt)))
    .filter((at) => !Number.isNaN(at));

  const last = running ? now : ends.length === 0 ? undefined : Math.max(...ends);
  return last === undefined ? undefined : Math.max(0, last - Math.min(...starts));
}

export function RunTimeline({ steps, runStatus, instruction }: RunTimelineProps) {
  // **Sorted here even though the server sorts.** `listSteps` orders by
  // `sequence`, but a component handed an array should not depend on where the
  // array came from - and this is the only form of the guarantee a test can
  // break, by handing it steps out of order. Copied rather than sorted in
  // place: the array belongs to the query cache.
  const ordered = [...(steps ?? [])].sort((a, b) => a.sequence - b.sequence);
  if (ordered.length === 0) return null;

  // Read once per render so every row measures against the same instant.
  const now = Date.now();
  const finished = runStatus !== undefined && isTerminalRunStatus(runStatus);
  const total = totalMs(ordered, now);

  // **`<details>` rather than a button and a piece of state.** §21.3 wants the
  // timeline collapsed once the work is done and still available for audit;
  // the element does that with keyboard support and the right semantics for
  // nothing, and `open` is a prop rather than a `useState` somebody has to
  // reset when the thread changes.
  return (
    <details open={!finished} className="text-sm">
      <summary className="cursor-pointer text-muted-foreground marker:text-muted-foreground">
        {finished
          ? `What the employee did — ${String(ordered.length)} step${ordered.length === 1 ? '' : 's'}${
              total === undefined ? '' : `, ${formatDuration(total)}`
            }`
          : instruction === undefined || instruction.trim() === ''
            ? 'Working on it'
            : `Working on ${truncate(instruction)}`}
      </summary>

      {/* **No `role="status"`, and that is a design decision rather than a
          test one.** One live region per screen: `RunNotice` already announces
          what the run is doing, and a second region beside it would interleave
          with it - two voices reporting the same run, in whichever order the
          DOM settles. This is the detail you look at, not the thing that
          speaks. (Announcing each step as it lands is worth doing on its own
          terms; the app has no `aria-live` anywhere to follow, so it is a
          decision to take deliberately rather than a side effect of this.) */}
      <ul aria-label="What the employee did" className="mt-2 space-y-1.5">
        {ordered.map((step) => (
          <StepRow key={step.stepId} step={step} now={now} />
        ))}
      </ul>
    </details>
  );
}

function StepRow({ step, now }: { readonly step: RunStepSummary; readonly now: number }) {
  const failed = step.status === 'FAILED';
  const elapsed = durationMs(step, now);

  return (
    <li className="flex items-baseline gap-2">
      <span aria-hidden="true" className={cn('w-3 shrink-0', failed && 'text-destructive')}>
        {markerFor(step)}
      </span>

      <span className="flex min-w-0 flex-col">
        <span className={cn('flex flex-wrap items-baseline gap-2', failed && 'text-destructive')}>
          {labelFor(step)}
          {elapsed !== undefined && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {formatDuration(elapsed)}
            </span>
          )}
          {failed && (
            <span className="text-xs text-destructive">({step.errorCode ?? 'failed'})</span>
          )}
        </span>

        {/* The runtime's own sentence. It names the app and the tool and never
            the arguments or the answer - `summary_redacted` is that promise. */}
        {step.summaryRedacted !== undefined && (
          <span className="text-xs text-muted-foreground">{step.summaryRedacted}</span>
        )}
      </span>
    </li>
  );
}

/** The heading is one line; a long first message must not become a paragraph. */
function truncate(text: string, max = 60): string {
  const trimmed = text.trim();
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1).trimEnd()}…`;
}
